import React, { useMemo, useState } from 'react';
import { Alert, Button, Form, Input, Select, Table, message } from 'antd';
import type { TableColumnsType } from 'antd';
import { useQuery, useMutation } from '@apollo/client';
import TablePaginationBar from 'components/TablePaginationBar';
import { showError } from 'utils/exceptionUtils';
import {
  GET_I18N_CONFIGS_WITH_PAGINATION,
  I18nConfigItem,
  I18nConfigPageResponse,
} from './graphql/queries/getI18nConfigsWithPagination';
import {
  CREATE_I18N_CONFIG,
  UPDATE_I18N_CONFIG,
  CreateI18nConfigInput,
  UpdateI18nConfigInput,
} from './graphql/mutations/i18nConfigAdminMutations';
import I18nConfigFormDrawer, { I18nConfigFormValues } from './components/I18nConfigFormDrawer';
import I18nConfigBatchCreateModal from './components/I18nConfigBatchCreateModal';

// popup_type 释义照抄后端 PopupType 枚举（api-server/.../warehouse/types/ScanResultType.kt），
// GraphQL 的 @GraphQLDescription 运行时读不到，只能前端照抄一份静态映射。
export const POPUP_TYPE_LABELS: Record<number, string> = {
  0: '成功（绿色边框，无弹窗）',
  1: '业务拦截（红色）',
  2: '需处理（橙色）',
  3: '临时提示（无色）',
  4: '成功并提示（橙色）',
  5: '红色全屏，无弹窗',
  6: '橙色全屏，无弹窗',
  7: '成功并展示内容（绿色边框+绿色弹窗）',
};

export const POPUP_TYPE_OPTIONS = Object.entries(POPUP_TYPE_LABELS).map(([value, label]) => ({
  label: `${value} - ${label}`,
  value: Number(value),
}));

type FilterValues = {
  messageKey?: string;
  popupType?: number;
  triggerScene?: string;
};

const previewText = (zh: string | null, en: string | null): string => {
  const text = zh || en;
  return text || '-';
};

const I18nConfigAdminPage: React.FC = () => {
  const [filterForm] = Form.useForm<FilterValues>();
  const [appliedFilters, setAppliedFilters] = useState<FilterValues>({});
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');
  const [selectedRecord, setSelectedRecord] = useState<I18nConfigItem | null>(null);
  const [batchModalVisible, setBatchModalVisible] = useState(false);

  const { data, loading, refetch } = useQuery<I18nConfigPageResponse>(GET_I18N_CONFIGS_WITH_PAGINATION, {
    variables: {
      filter: {
        messageKey: appliedFilters.messageKey || undefined,
        popupType: appliedFilters.popupType ?? undefined,
        triggerScene: appliedFilters.triggerScene || undefined,
        isDeleted: false,
      },
      pageRequest: { page: pagination.current, size: pagination.pageSize },
    },
    fetchPolicy: 'network-only',
  });

  const [createI18nConfig] = useMutation(CREATE_I18N_CONFIG);
  const [updateI18nConfig] = useMutation(UPDATE_I18N_CONFIG);

  const handleFilter = () => {
    setAppliedFilters(filterForm.getFieldsValue());
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleReset = () => {
    filterForm.resetFields();
    setAppliedFilters({});
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const showNewDrawer = () => {
    setSelectedRecord(null);
    setDrawerMode('create');
    setDrawerVisible(true);
  };

  const showEditDrawer = (record: I18nConfigItem) => {
    setSelectedRecord(record);
    setDrawerMode('edit');
    setDrawerVisible(true);
  };

  const handleDrawerSubmit = async (values: I18nConfigFormValues) => {
    try {
      if (drawerMode === 'create') {
        // 新建态表单校验已保证 messageKey 必填，此处转换为后端要求的必填类型
        const input = values as CreateI18nConfigInput;
        await createI18nConfig({ variables: { input } });
        message.success('创建成功');
      } else {
        // 编辑态 values 不含 messageKey（由 I18nConfigFormDrawer 提交前剔除），不可改名
        const input: UpdateI18nConfigInput = { id: selectedRecord!.id, ...values };
        await updateI18nConfig({ variables: { input } });
        message.success('更新成功');
      }
      setDrawerVisible(false);
      refetch();
    } catch (e: any) {
      showError(e);
    }
  };

  const columns: TableColumnsType<I18nConfigItem> = useMemo(
    () => [
      { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
      { title: 'messageKey', dataIndex: 'messageKey', key: 'messageKey', width: 260 },
      {
        title: '弹窗级别',
        dataIndex: 'popupType',
        key: 'popupType',
        width: 100,
        render: (v: number) => <span>{v}</span>,
      },
      {
        title: '中文预览',
        key: 'previewZh',
        render: (_: any, record: I18nConfigItem) =>
          previewText(record.titleZh || record.contentZh, record.titleEn || record.contentEn),
      },
      {
        title: '触发场景',
        dataIndex: 'triggerScene',
        key: 'triggerScene',
        render: (v: string | null) => v || '-',
      },
      {
        title: '备注',
        dataIndex: 'remark',
        key: 'remark',
        render: (v: string | null) => v || '-',
      },
      {
        title: '操作人ID',
        dataIndex: 'updatedBy',
        key: 'updatedBy',
      },
      {
        title: '操作',
        key: 'actions',
        width: 100,
        render: (_: any, record: I18nConfigItem) => (
          <Button type="link" style={{ paddingLeft: 0 }} onClick={() => showEditDrawer(record)}>
            编辑
          </Button>
        ),
      },
    ],
    []
  );

  const tableData = data?.getI18nConfigsWithPagination?.content ?? [];
  const totalElements = data?.getI18nConfigsWithPagination?.totalElements ?? 0;

  return (
    <div>
      <Alert
        type="info"
        showIcon
        className="mb-3"
        message="i18n_config 文案配置说明"
        description={
          <div className="text-xs leading-5">
            <div>
              用途：维护各端扫描提示/轨迹文案的多语言配置，替代手写 SQL 迁移脚本上线新文案。
            </div>
            <div>
              规则：messageKey 创建后不可改名（运行时查找键，改名会导致所有引用旧 key 的代码读不到文案）；
              同一 messageKey 不能存在两条有效记录，已存在时新增会被拒绝，请改用编辑；
              title/content 六个文案字段至少需要填写一项。
            </div>
            <div>
              新增/编辑保存后不会立即生效，所有实例统一通过定时预热机制在最长 30 分钟内生效。
            </div>
          </div>
        }
      />
      <Form form={filterForm} layout="inline" className="py-3 flex flex-wrap gap-2">
        <Form.Item name="messageKey" style={{ marginBottom: 0 }}>
          <Input placeholder="messageKey" allowClear style={{ width: 220 }} />
        </Form.Item>
        <Form.Item name="popupType" style={{ marginBottom: 0 }}>
          <Select placeholder="弹窗级别" options={POPUP_TYPE_OPTIONS} allowClear style={{ width: 160 }} />
        </Form.Item>
        <Form.Item name="triggerScene" style={{ marginBottom: 0 }}>
          <Input placeholder="触发场景" allowClear style={{ width: 200 }} />
        </Form.Item>
        <Form.Item style={{ marginBottom: 0 }}>
          <Button type="primary" onClick={handleFilter}>
            查询
          </Button>
        </Form.Item>
        <Form.Item style={{ marginBottom: 0 }}>
          <Button onClick={handleReset}>重置</Button>
        </Form.Item>
      </Form>

      <div className="flex justify-end gap-2 mb-3">
        <Button onClick={() => setBatchModalVisible(true)}>批量新增</Button>
        <Button type="primary" onClick={showNewDrawer}>
          <span className="font-bold">新建</span>
        </Button>
      </div>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={tableData}
        pagination={false}
        scroll={{ x: 'max-content' }}
        size="middle"
        tableLayout="auto"
        className="mb-4"
      />

      <TablePaginationBar
        total={totalElements}
        current={pagination.current}
        pageSize={pagination.pageSize}
        onChange={(page, size) => setPagination({ current: page, pageSize: size })}
      />

      <I18nConfigFormDrawer
        visible={drawerVisible}
        mode={drawerMode}
        initialValues={selectedRecord ?? undefined}
        onClose={() => setDrawerVisible(false)}
        onSubmit={handleDrawerSubmit}
      />

      <I18nConfigBatchCreateModal
        visible={batchModalVisible}
        onClose={() => setBatchModalVisible(false)}
        onDone={() => {
          setBatchModalVisible(false);
          refetch();
        }}
      />
    </div>
  );
};

export default I18nConfigAdminPage;
